import React from 'react';

import Question from '@commons/Question';

import { DesktopDNDLayout, MobileDNDLayout } from '@components/RankingOrder/DNDLayout';
import { translations as translationsDNDLayout } from '@components/RankingOrder/DNDLayout/constants';
import DropdownLayout from '@components/RankingOrder/DropdownLayout';
import { translations as translationsDropdownLayout } from '@components/RankingOrder/DropdownLayout/constants';
import { LayoutVariant } from '@components/RankingOrder/constants';

import TouchDetector from '@models/touchDetector';

import { isRankingOrderAccessibilityEnabled } from '@services/featureFlags';

import styles from './rankingOrder.scss';

export interface RankingOrderItem {
  id: string;
  text: string;
}

interface RankingOrderProps {
  // Id property can be 0
  id?: number | null;
  caption?: string;
  row?: RankingOrderItem[];
  selected?: RankingOrderItem[];
  unselected?: RankingOrderItem[];
  selectedFormKey?: string;
  notSelectedFormKey?: string;
  layout?: string;
  setAlternativeAt: (position: number, itemId: string) => void;
  firstSelectionOnDnD: (itemId: string) => void;
  unselectAlternative: (itemId: string) => void;
  validationEmpty?: string;
  validationFailed?: string;
  requiredField?: boolean;
  validationMessages?: string[];
}

class RankingOrder extends React.Component<RankingOrderProps> {
  constructor(props) {
    super(props);
    this.wrapperRef = null;
    this.state = {
      currentlyOnTouchInteractions: TouchDetector.hasTouchAbility()
    };
  }

  setWrapperRef = (ref) => {
    this.wrapperRef = ref;
  };

  registerClickInteraction = () => {
    if (this.state.currentlyOnTouchInteractions) {
      this.setState({ currentlyOnTouchInteractions: false });
    }
  };

  registerTouchInteraction = () => {
    if (!this.state.currentlyOnTouchInteractions) {
      this.setState({ currentlyOnTouchInteractions: true });
    }
  };

  componentDidMount() {
    this.wrapperRef.addEventListener('click', this.registerClickInteraction);
    this.wrapperRef.addEventListener('touchstart', this.registerTouchInteraction);
  }

  componentWillUnmount() {
    this.wrapperRef.removeEventListener('click', this.registerClickInteraction);
    this.wrapperRef.removeEventListener('touchstart', this.registerTouchInteraction);
  }

  getFormHiddenInputs() {
    const { selected, unselected, selectedFormKey, notSelectedFormKey } = this.props;

    let answered = selected.map((item, idx) =>
      item ? (
        <input
          key={`selected${idx}${item.id}`}
          type="hidden"
          name={`${selectedFormKey}`}
          value={item.id}
        />
      ) : (
        <input key={`selected${idx}`} type="hidden" name={`${selectedFormKey}`} />
      )
    );

    if (!answered.length) {
      answered = [<input key="selected" type="hidden" name={`${selectedFormKey}`} />];
    }

    let discarded = unselected.map((item, idx) =>
      item ? (
        <input
          key={`unselected${idx}${item.id}`}
          type="hidden"
          name={`${notSelectedFormKey}`}
          value={item.id}
        />
      ) : (
        <input key={`unselected${idx}`} type="hidden" name={`${notSelectedFormKey}`} />
      )
    );

    if (!discarded.length) {
      discarded = [<input key="unselected" type="hidden" name={`${notSelectedFormKey}`} />];
    }

    return [...answered, ...discarded];
  }

  render() {
    const {
      id,
      caption,
      row,
      selected,
      layout,
      setAlternativeAt,
      firstSelectionOnDnD,
      unselectAlternative,
      validationEmpty,
      validationFailed,
      requiredField,
      validationMessages
    } = this.props;

    const DNDLayout = this.state.currentlyOnTouchInteractions ? MobileDNDLayout : DesktopDNDLayout;

    const questionId = id !== null ? id.toString() : '';

    const ariaDescribedBy =
      questionId >= 0 && isRankingOrderAccessibilityEnabled()
        ? { 'aria-describedby': `${questionId}_HelpText  ${questionId}_Label` }
        : {};

    const helpText =
      layout === LayoutVariant.DROPDOWN
        ? translationsDropdownLayout().DROPDOWN_REORDERING_INSTRUCTIONS
        : translationsDNDLayout().DND_REORDERING_INSTRUCTIONS;

    const helpTextId = `${questionId}_HelpText`;
    const labelId = `${questionId}_Label`;

    const captionPrefix = isRankingOrderAccessibilityEnabled() ? (
      <div id={`${questionId}`} tabIndex={0} {...ariaDescribedBy}>
        <span id={helpTextId} className={styles.visuallyHidden}>
          {helpText}
        </span>
        <span aria-hidden id={labelId} className={styles.visuallyHidden}>
          {caption}
        </span>
      </div>
    ) : null;

    return (
      <div className="questionBlock rankingQuestion" ref={this.setWrapperRef}>
        <Question
          ariaId={questionId}
          key="question"
          caption={caption}
          validationEmpty={validationEmpty}
          validationFailed={validationFailed}
          requiredField={requiredField}
          validationMessages={validationMessages}
          captionId={String(id)}
          hasLegendCaption={false}
          captionPrefix={captionPrefix}
        />
        {layout === LayoutVariant.DND ? (
          <DNDLayout
            originalItems={row}
            selectedItems={selected}
            reorderList={setAlternativeAt}
            firstSelection={firstSelectionOnDnD}
            caption={caption}
          />
        ) : (
          <DropdownLayout
            originalItems={row}
            selectedItems={selected}
            setAlternativeAt={setAlternativeAt}
            unselectAlternative={unselectAlternative}
          />
        )}
        {this.getFormHiddenInputs()}
      </div>
    );
  }
}

RankingOrder.defaultProps = {
  id: null,
  caption: '',
  row: [],
  selected: [],
  unselected: [],
  selectedFormKey: '',
  notSelectedFormKey: '',
  layout: LayoutVariant.DND
};

export default RankingOrder;
